// Parent details interface

import { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useFirebase } from './FirebaseContextProvider';
import { useModal } from './ModalProvider';
import UserCardTemplate from './UserCardTemplate';
import GuidedTour from './GuidedTour';
import PropItem from './PropItem';
import PropItemsBlock from './PropItemsBlock';
import ParentCardEdit from './ParentCardEdit';
import PlayersList from './PlayersList';
import TournamentsList from './TournamentsList';
import WaiverContent from './WaiverContent';
import { waiverGetUnsigned } from '../utils/firebase';
import { useEntities } from '../utils/hooks';
import {
  storageWaiverLastShown,
  waiverPromptIntervalHours,
} from '../utils/consts';
import { TourStatuses } from '../utils/enums';
import placeholderPerson from '../assets/images/placeholder-person.png';

const steps = [
  {
    target: '.tabs-header-item-tournaments',
    title: 'Tournaments',
    content: "See all tournaments your children's teams are admitted to here",
  },
  {
    target: '.tabs-header-item-players',
    title: 'Your players',
    content: 'Here you can see your children and their status',
  },
];

export default function ParentCard({ parent }) {
  const {
    user,
    userIsAdmin,
    userIsParent,
    usersById,
    childrenByParentId,
    isParentsLoaded,
  } = useFirebase();
  const { openModal } = useModal();
  const { id, name, phone, email } = parent;

  // Every child this parent is linked to, whatever the link status: the prompt
  // names the child, and `waiverGetUnsigned` answers for pending links too.
  // The provider only registers the approved ones (they are what `myTeamIds`
  // is built from), so an unregistered child used to leave the prompt with no
  // name to show and silently skipped it.
  const childIds = useMemo(
    () => (childrenByParentId.get(id) ?? []).map((child) => child.id),
    [childrenByParentId, id],
  );
  const { isResolved: areChildrenResolved } = useEntities('users', childIds);

  const isTheUser = user.id === id;
  const [run, setRun] = useState(false);
  const checkedRef = useRef(false);

  const tourIsResolved =
    !isTheUser ||
    !userIsParent ||
    (user?.tourStatusesLoaded &&
      user.tourStatuses?.some(
        ({ id, status }) =>
          id === 'parent-card-v2' &&
          (status === TourStatuses.COMPLETED ||
            status === TourStatuses.SKIPPED),
      ));

  useEffect(() => {
    if (parent.name) setRun(true);
  }, [parent.name]);

  useEffect(() => {
    // Opening before the links and the child documents behind them land would
    // find no name to show
    if (
      !isTheUser ||
      checkedRef.current ||
      !isParentsLoaded ||
      !areChildrenResolved ||
      !name ||
      !tourIsResolved
    )
      return;

    const last = localStorage.getItem(storageWaiverLastShown);
    const now = Date.now();
    const intervalMs = waiverPromptIntervalHours * 60 * 60 * 1000;

    if (last && now - new Date(last).getTime() < intervalMs) return;

    // Reports whether a prompt actually went up: a child whose document did
    // not resolve has no name to show, and stamping "last shown" for a prompt
    // nobody saw would suppress it for the whole interval
    const openWaiver = (playerIds) => {
      const playerId = playerIds.find((pid) => usersById.has(pid));
      if (!playerId) return false;

      const player = usersById.get(playerId);

      openModal({
        header: 'Liability Waiver',
        body: (
          <WaiverContent
            playerName={player.name}
            playerId={playerId}
            onSigned={(signedId) => {
              const remaining = playerIds.filter((pid) => pid !== signedId);
              if (remaining.length > 0) {
                openWaiver(remaining);
              }
            }}
          />
        ),
      });

      return true;
    };

    checkedRef.current = true;

    const checkWaivers = async () => {
      try {
        const res = await waiverGetUnsigned({ parentId: user.id });
        const players = res.data?.players || {};
        const ids = Object.keys(players).filter((id) => !players[id].liability);
        if (ids.length > 0 && openWaiver(ids)) {
          localStorage.setItem(
            storageWaiverLastShown,
            new Date().toISOString(),
          );
        }
      } catch (err) {
        toast.error('Failed to check waivers');
      }
    };

    checkWaivers();
  }, [
    isTheUser,
    user.id,
    usersById,
    openModal,
    isParentsLoaded,
    areChildrenResolved,
    name,
    tourIsResolved,
  ]);

  return (
    <GuidedTour
      steps={isTheUser && userIsParent ? steps : []}
      run={run}
      tourKey='parent-card-v2'
    >
      <UserCardTemplate
        header='Parent'
        className='parent-card'
        backBtn={!isTheUser}
        isTheUser={isTheUser}
        imgPath={parent.imgPath}
        imgThumbPath={parent.imgThumbPath}
        imgTitle={name}
        name={name}
        placeholder={placeholderPerson}
        details={
          <PropItemsBlock>
            <PropItem prop='Phone' value={phone} />
            <PropItem prop='e-mail' value={email} />
          </PropItemsBlock>
        }
        editForm={
          (userIsAdmin || isTheUser) && <ParentCardEdit parent={parent} />
        }
        tabs={[
          {
            title: 'Tournaments',
            hash: '#tournaments',
            component: <TournamentsList userId={id} />,
          },
          {
            title: 'Players',
            hash: '#players',
            component: <PlayersList userId={id} />,
          },
        ]}
      />
    </GuidedTour>
  );
}
